/**
 * Content Classification Service
 *
 * Classifies content from different platforms into sections
 * based on hashtags, keywords, and manual overrides
 */

import config from '../config';

class ContentClassifier {
  constructor() {
    this.manualOverrides = new Map();
  }

  /**
   * Classify an array of content items into sections
   * @param {Array} content - Array of content from any platform
   * @returns {Object} - Classified content by section
   */
  classifyContent(content) {
    const classified = {
      hero: [],
      gym: [],
      office: [],
      closet: [],
      unclassified: [],
    };

    content.forEach(item => {
      // Check for manual override first
      const override = this.manualOverrides.get(item.id);
      if (override) {
        classified[override].push(item);
        return;
      }

      // Auto-classify based on hashtags
      const section = this.detectSection(item);
      classified[section].push(item);
    });

    // Apply FIFO limits
    return this.applyLimits(classified);
  }

  /**
   * Detect which section content belongs to based on hashtags
   */
  detectSection(item) {
    const hashtags = this.getItemHashtags(item);

    // Check gym hashtags
    if (this.hasMatchingHashtag(hashtags, config.hashtags.gym)) {
      return 'gym';
    }

    // Check office hashtags
    if (this.hasMatchingHashtag(hashtags, config.hashtags.office)) {
      return 'office';
    }

    // Check closet hashtags
    if (this.hasMatchingHashtag(hashtags, config.hashtags.closet)) {
      return 'closet';
    }

    // Default to unclassified
    return 'unclassified';
  }

  /**
   * Get hashtags from different content types
   */
  getItemHashtags(item) {
    // Instagram has hashtags property
    if (item.hashtags) {
      return item.hashtags;
    }

    // YouTube has tags
    if (item.tags) {
      return item.tags.map(tag => `#${tag.toLowerCase()}`);
    }

    // TikTok might have hashtags in caption
    if (item.caption) {
      return this.extractHashtags(item.caption);
    }

    return [];
  }

  /**
   * Extract hashtags from text
   */
  extractHashtags(text) {
    if (!text) return [];
    const hashtagRegex = /#[\w]+/g;
    const matches = text.match(hashtagRegex);
    return matches ? matches.map(tag => tag.toLowerCase()) : [];
  }

  /**
   * Check if item has matching hashtag
   */
  hasMatchingHashtag(itemHashtags, targetHashtags) {
    return itemHashtags.some(itemTag =>
      targetHashtags.some(targetTag =>
        itemTag.toLowerCase().includes(targetTag.toLowerCase().replace('#', ''))
      )
    );
  }

  /**
   * Apply FIFO limits to sections
   * Keep only the most recent items up to the limit
   */
  applyLimits(classified) {
    Object.keys(config.limits).forEach(section => {
      if (classified[section]) {
        // Sort by timestamp (most recent first)
        classified[section].sort((a, b) => {
          const dateA = new Date(a.publishedAt || a.timestamp);
          const dateB = new Date(b.publishedAt || b.timestamp);
          return dateB - dateA;
        });

        // Keep only up to limit
        classified[section] = classified[section].slice(0, config.limits[section]);
      }
    });

    return classified;
  }

  /**
   * Set manual override for content classification
   * @param {string} contentId - ID of the content
   * @param {string} section - Section to assign to (gym, office, closet, hero)
   */
  setManualOverride(contentId, section) {
    if (!['gym', 'office', 'closet', 'hero'].includes(section)) {
      throw new Error(`Invalid section: ${section}`);
    }
    this.manualOverrides.set(contentId, section);
  }

  /**
   * Remove manual override
   */
  removeManualOverride(contentId) {
    this.manualOverrides.delete(contentId);
  }

  /**
   * Get all manual overrides
   */
  getManualOverrides() {
    return Object.fromEntries(this.manualOverrides);
  }

  /**
   * Load manual overrides from storage
   * In production, this would load from a database or CMS
   */
  loadOverrides(overrides) {
    Object.entries(overrides).forEach(([id, section]) => {
      this.manualOverrides.set(id, section);
    });
  }

  /**
   * Clear all manual overrides
   */
  clearOverrides() {
    this.manualOverrides.clear();
  }
}

export default new ContentClassifier();
